<template>
  <div class="tools-container">
    <!-- 页面头部 -->
    <div class="page-header">
      <h1 class="page-title">Analysis Tools</h1>
      <p class="page-subtitle">Professional genomic data analysis toolkit</p>
    </div>

    <!-- 主工具窗口 -->
    <div class="tools-window">
      <!-- 左侧工具导航 -->
      <div class="tools-sidebar">
        <div class="sidebar-header">
          <h3>Tools</h3>
        </div>
        <div class="tools-nav">
          <div
            v-for="tool in tools"
            :key="tool.id"
            class="nav-item"
            :class="{ active: activeToolId === tool.id }"
            @click="switchTool(tool.id)"
          >
            <div class="nav-icon">{{ tool.icon }}</div>
            <div class="nav-content">
              <div class="nav-title">{{ tool.name }}</div>
              <div class="nav-subtitle">{{ tool.subtitle }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 中间操作区域 -->
      <div class="main-content">
        <!-- 基因区域分析器 -->
        <div v-if="activeToolId === 'gene-analyzer'" class="tool-panel">
          <div class="panel-header">
            <h2>🧬 Gene Region Analyzer</h2>
            <p>Analyze variants within and around specific genes</p>
          </div>

          <div class="panel-body">
            <el-form :model="geneForm" label-position="top" size="large">
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="Gene ID / Ensembl ID">
                    <el-input
                      v-model="geneForm.geneId"
                      placeholder="e.g., BRCA1 or ENSG00000012048"
                      size="large"
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="6">
                  <el-form-item label="Upstream (bp)">
                    <el-input-number
                      v-model="geneForm.upstream"
                      :min="0"
                      :step="1000"
                      style="width: 100%"
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="6">
                  <el-form-item label="Downstream (bp)">
                    <el-input-number
                      v-model="geneForm.downstream"
                      :min="0"
                      :step="1000"
                      style="width: 100%"
                    />
                  </el-form-item>
                </el-col>
              </el-row>

              <el-form-item>
                <el-button
                  type="primary"
                  size="large"
                  @click="analyzeGeneRegion"
                  :loading="geneLoading"
                  style="width: 200px"
                >
                  <Search />
                  Analyze Region
                </el-button>
              </el-form-item>
            </el-form>

            <!-- 结果显示 -->
            <div v-if="geneResults" class="results-section">
              <el-divider content-position="left">Analysis Results</el-divider>
              <el-alert
                :title="`Found ${geneResults.variant_count} variants in region ${geneResults.query_region.start.toLocaleString()}-${geneResults.query_region.end.toLocaleString()}`"
                type="success"
                show-icon
                style="margin-bottom: 20px"
              />

              <el-table
                :data="geneResults.variants.slice(0, 15)"
                style="width: 100%"
                stripe
                border
                max-height="400"
              >
                <el-table-column prop="variant_id" label="Variant ID" width="140" />
                <el-table-column prop="position" label="Position" width="120">
                  <template #default="scope">
                    {{ scope.row.position.toLocaleString() }}
                  </template>
                </el-table-column>
                <el-table-column prop="ref_allele" label="Ref" width="80" />
                <el-table-column prop="alt_allele" label="Alt" width="80" />
                <el-table-column prop="rsid" label="rsID" min-width="120" />
                <el-table-column label="Actions" width="100">
                  <template #default="scope">
                    <el-button link type="primary" size="small">Details</el-button>
                  </template>
                </el-table-column>
              </el-table>

              <p v-if="geneResults.variants.length > 15" class="show-more-hint">
                Showing first 15 results of {{ geneResults.variant_count }} total variants
              </p>
            </div>
          </div>
        </div>

        <!-- 批量变异查询 -->
        <div v-if="activeToolId === 'batch-query'" class="tool-panel">
          <div class="panel-header">
            <h2>🔍 Batch Variant Lookup</h2>
            <p>Query multiple variants simultaneously (max 1000)</p>
          </div>

          <div class="panel-body">
            <el-form :model="batchForm" label-position="top" size="large">
              <el-form-item label="Variant IDs">
                <el-input
                  v-model="batchForm.variantIds"
                  type="textarea"
                  :rows="8"
                  placeholder="Enter variant IDs, one per line..."
                  style="font-family: monospace"
                />
                <div class="input-hint">
                  Enter variant IDs separated by new lines. Example: var_001, var_002, etc.
                </div>
              </el-form-item>

              <el-form-item>
                <el-button
                  type="primary"
                  size="large"
                  @click="batchQuery"
                  :loading="batchLoading"
                  style="width: 200px"
                >
                  <Operation />
                  Query Variants
                </el-button>
                <el-button
                  size="large"
                  @click="clearBatchForm"
                  style="margin-left: 10px"
                >
                  Clear
                </el-button>
              </el-form-item>
            </el-form>

            <!-- 结果显示 -->
            <div v-if="batchResults" class="results-section">
              <el-divider content-position="left">Query Results</el-divider>
              <el-alert
                :title="`Found ${batchResults.found} out of ${batchResults.requested} variants`"
                :type="batchResults.found === batchResults.requested ? 'success' : 'warning'"
                show-icon
                style="margin-bottom: 20px"
              />

              <el-table
                :data="batchResults.data"
                style="width: 100%"
                stripe
                border
                max-height="400"
              >
                <el-table-column prop="variant_id" label="Variant ID" width="140" />
                <el-table-column prop="chrom" label="Chr" width="80" />
                <el-table-column prop="position" label="Position" width="120">
                  <template #default="scope">
                    {{ scope.row.position?.toLocaleString() }}
                  </template>
                </el-table-column>
                <el-table-column prop="ref_allele" label="Ref" width="80" />
                <el-table-column prop="alt_allele" label="Alt" width="80" />
                <el-table-column prop="rsid" label="rsID" min-width="120" />
                <el-table-column label="Actions" width="100">
                  <template #default="scope">
                    <el-button link type="primary" size="small">Details</el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>

        <!-- 染色体统计 -->
        <div v-if="activeToolId === 'chromosome-stats'" class="tool-panel">
          <div class="panel-header">
            <h2>📊 Chromosome Statistics</h2>
            <p>Visualize variant distribution across chromosomes</p>
          </div>

          <div class="panel-body">
            <el-form label-position="top" size="large">
              <el-form-item>
                <el-button
                  type="primary"
                  size="large"
                  @click="loadChromosomeStats"
                  :loading="chromStatsLoading"
                  style="width: 200px"
                >
                  <PieChart />
                  Generate Chart
                </el-button>
              </el-form-item>
            </el-form>

            <!-- 图表显示 -->
            <div v-if="chromStatsData" class="chart-section">
              <el-divider content-position="left">Chromosome Distribution</el-divider>
              <div class="chart-container">
                <canvas ref="chromStatsChart" width="800" height="400"></canvas>
              </div>

              <!-- 统计表格 -->
              <div class="stats-table">
                <el-table
                  :data="chromStatsData.slice(0, 10)"
                  style="width: 100%; margin-top: 20px"
                  size="small"
                >
                  <el-table-column prop="chrom" label="Chromosome" width="100" />
                  <el-table-column prop="variant_count" label="Variants" width="120">
                    <template #default="scope">
                      {{ scope.row.variant_count.toLocaleString() }}
                    </template>
                  </el-table-column>
                  <el-table-column prop="rsid_count" label="rsIDs" width="120">
                    <template #default="scope">
                      {{ scope.row.rsid_count?.toLocaleString() }}
                    </template>
                  </el-table-column>
                  <el-table-column prop="min_position" label="Min Position" width="120">
                    <template #default="scope">
                      {{ scope.row.min_position?.toLocaleString() }}
                    </template>
                  </el-table-column>
                  <el-table-column prop="max_position" label="Max Position" width="120">
                    <template #default="scope">
                      {{ scope.row.max_position?.toLocaleString() }}
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧帮助面板 -->
      <div class="help-panel">
        <div class="help-header">
          <h3>{{ currentTool.name }}</h3>
        </div>
        <div class="help-content">
          <div class="help-section">
            <h4>Description</h4>
            <p>{{ currentTool.description }}</p>
          </div>

          <div class="help-section">
            <h4>Usage Instructions</h4>
            <div v-html="currentTool.instructions"></div>
          </div>

          <div class="help-section">
            <h4>Examples</h4>
            <div v-html="currentTool.examples"></div>
          </div>

          <div class="help-section" v-if="currentTool.limitations">
            <h4>Limitations</h4>
            <div v-html="currentTool.limitations"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import {
  Search,
  Operation,
  PieChart
} from '@element-plus/icons-vue'
import Chart from 'chart.js/auto'

export default {
  name: 'Tools',
  components: {
    Search,
    Operation,
    PieChart
  },
  setup() {
    // 当前激活的工具ID
    const activeToolId = ref('gene-analyzer')

    // API基础配置
    const API_BASE = '/api'
    // 工具定义
    const tools = ref([
      {
        id: 'gene-analyzer',
        name: 'Gene Region Analyzer',
        subtitle: 'Query gene variants',
        icon: '🧬',
        description: 'Analyze genetic variants within and around specific genes, including upstream and downstream regions.',
        instructions: `
          <ol>
            <li>Enter a gene ID (e.g., BRCA1) or Ensembl ID (e.g., ENSG00000012048)</li>
            <li>Set upstream and downstream regions in base pairs</li>
            <li>Click "Analyze Region" to search for variants</li>
            <li>Review results in the table below</li>
          </ol>
        `,
        examples: `
          <ul>
            <li><strong>BRCA1</strong> - Breast cancer gene analysis</li>
            <li><strong>TP53</strong> - Tumor suppressor gene</li>
            <li><strong>ENSG00000012048</strong> - Using Ensembl ID</li>
          </ul>
        `,
        limitations: `
          <ul>
            <li>Maximum region size: 10MB</li>
            <li>Results limited to first 15 variants in display</li>
            <li>Gene must exist in the database</li>
          </ul>
        `
      },
      {
        id: 'batch-query',
        name: 'Batch Variant Lookup',
        subtitle: 'Query multiple variants',
        icon: '🔍',
        description: 'Query multiple genetic variants simultaneously by providing a list of variant IDs.',
        instructions: `
          <ol>
            <li>Enter variant IDs in the text area, one per line</li>
            <li>Maximum 1000 variant IDs per query</li>
            <li>Click "Query Variants" to execute the search</li>
            <li>Results show found vs requested variants</li>
          </ol>
        `,
        examples: `
          <ul>
            <li><strong>var_001</strong></li>
            <li><strong>rs123456</strong></li>
            <li><strong>chr1_12345_A_T</strong></li>
          </ul>
        `,
        limitations: `
          <ul>
            <li>Maximum 1000 variant IDs per batch</li>
            <li>Variant IDs must exist in database</li>
            <li>One variant ID per line</li>
          </ul>
        `
      },
      {
        id: 'chromosome-stats',
        name: 'Chromosome Statistics',
        subtitle: 'Visualize data distribution',
        icon: '📊',
        description: 'Generate comprehensive statistics and visualizations showing variant distribution across all chromosomes.',
        instructions: `
          <ol>
            <li>Click "Generate Chart" to load chromosome statistics</li>
            <li>View the bar chart showing variant counts per chromosome</li>
            <li>Review detailed statistics in the table below</li>
            <li>Data includes variant counts, rsID counts, and position ranges</li>
          </ol>
        `,
        examples: `
          <ul>
            <li><strong>Chromosome 1</strong> - Usually has the most variants</li>
            <li><strong>Chromosome Y</strong> - Typically has the fewest variants</li>
            <li><strong>Position ranges</strong> - Shows chromosome spans</li>
          </ul>
        `,
        limitations: `
          <ul>
            <li>Data reflects current database content</li>
            <li>Chart limited to first 25 chromosomes</li>
            <li>Statistics updated in real-time</li>
          </ul>
        `
      }
    ])

    // 当前工具的计算属性
    const currentTool = computed(() => {
      return tools.value.find(tool => tool.id === activeToolId.value) || tools.value[0]
    })

    // 基因区域分析相关状态
    const geneLoading = ref(false)
    const geneResults = ref(null)
    const geneForm = reactive({
      geneId: '',
      upstream: 5000,
      downstream: 5000
    })

    // 批量查询相关状态
    const batchLoading = ref(false)
    const batchResults = ref(null)
    const batchForm = reactive({
      variantIds: ''
    })

    // 染色体统计相关状态
    const chromStatsLoading = ref(false)
    const chromStatsData = ref(null)
    const chromStatsChart = ref(null)
    let chromChart = null

    // 辅助函数：构建查询参数
    const buildQueryString = (params = {}) => {
      const searchParams = new URLSearchParams()
      Object.keys(params).forEach(key => {
        if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
          searchParams.append(key, params[key])
        }
      })
      const queryString = searchParams.toString()
      return queryString ? `?${queryString}` : ''
    }

    // 辅助函数：构建API URL
    const buildApiUrl = (endpoint, params = {}) => {
      return `${API_BASE}${endpoint}${buildQueryString(params)}`
    }

    // 切换工具
    const switchTool = (toolId) => {
      activeToolId.value = toolId
      // 清除之前的结果
      geneResults.value = null
      batchResults.value = null
      chromStatsData.value = null
    }

    // 基因区域分析
    const analyzeGeneRegion = async () => {
      if (!geneForm.geneId.trim()) {
        ElMessage.warning('Please enter a gene ID or Ensembl ID')
        return
      }

      geneLoading.value = true
      try {
        const params = {
          gene_id: geneForm.geneId,
          upstream: geneForm.upstream,
          downstream: geneForm.downstream
        }
        const response = await fetch(buildApiUrl('/variants/by-gene-region', params))
        const data = await response.json()

        if (response.ok) {
          geneResults.value = data
          ElMessage.success(`Found ${data.variant_count} variants in gene region`)
        } else {
          ElMessage.error(data.error || 'Failed to analyze gene region')
        }
      } catch (error) {
        console.error('Error analyzing gene region:', error)
        ElMessage.error('Network error occurred')
      } finally {
        geneLoading.value = false
      }
    }

    // 批量变异查询
    const batchQuery = async () => {
      const ids = batchForm.variantIds.split('\n')
        .filter(id => id.trim())
        .map(id => id.trim())

      if (ids.length === 0) {
        ElMessage.warning('Please enter at least one variant ID')
        return
      }

      if (ids.length > 1000) {
        ElMessage.warning('Maximum 1000 variant IDs allowed')
        return
      }

      batchLoading.value = true
      try {
        const response = await fetch(`${API_BASE}/batch/variants`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ variant_ids: ids })
        })

        const data = await response.json()

        if (response.ok) {
          batchResults.value = data
          ElMessage.success(`Found ${data.found} out of ${data.requested} variants`)
        } else {
          ElMessage.error(data.error || 'Batch query failed')
        }
      } catch (error) {
        console.error('Error in batch query:', error)
        ElMessage.error('Network error occurred')
      } finally {
        batchLoading.value = false
      }
    }

    // 清除批量查询表单
    const clearBatchForm = () => {
      batchForm.variantIds = ''
      batchResults.value = null
    }

    // 染色体统计图表加载
    const loadChromosomeStats = async () => {
      chromStatsLoading.value = true
      try {
        const response = await fetch(`${API_BASE}/stats/variants-by-chromosome`)
        const data = await response.json()

        if (response.ok) {
          chromStatsData.value = data.data
          await nextTick()
          renderChromosomeChart(data.data)
          ElMessage.success('Chromosome statistics loaded successfully')
        } else {
          ElMessage.error('Failed to load chromosome statistics')
        }
      } catch (error) {
        console.error('Error loading chromosome stats:', error)
        ElMessage.error('Network error occurred')
      } finally {
        chromStatsLoading.value = false
      }
    }

    // 渲染染色体统计图表
    const renderChromosomeChart = (data) => {
      if (chromChart) {
        chromChart.destroy()
      }

      const ctx = chromStatsChart.value.getContext('2d')
      chromChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: data.map(item => `Chr ${item.chrom}`),
          datasets: [{
            label: 'Variant Count',
            data: data.map(item => item.variant_count),
            backgroundColor: 'rgba(31, 58, 147, 0.8)',
            borderColor: 'rgba(31, 58, 147, 1)',
            borderWidth: 1,
            borderRadius: 4
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: 'Variant Distribution by Chromosome',
              font: {
                size: 16
              }
            },
            legend: {
              display: false
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Chromosome'
              }
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Number of Variants'
              }
            }
          }
        }
      })
    }

    return {
      // 工具相关
      tools,
      activeToolId,
      currentTool,
      switchTool,

      // 基因区域分析
      geneLoading,
      geneResults,
      geneForm,
      analyzeGeneRegion,

      // 批量查询
      batchLoading,
      batchResults,
      batchForm,
      batchQuery,
      clearBatchForm,

      // 染色体统计
      chromStatsLoading,
      chromStatsData,
      chromStatsChart,
      loadChromosomeStats
    }
  }
}
</script>

<style scoped>
/* 页面容器 */
.tools-container {
  min-height: calc(100vh - 100px);
  padding: 20px;
  background: #f5f7fa;
}

/* 页面头部 */
.page-header {
  text-align: center;
  margin-bottom: 30px;
}

.page-title {
  font-size: 32px;
  color: #1f3a93;
  margin-bottom: 8px;
  font-weight: 600;
}

.page-subtitle {
  font-size: 16px;
  color: #666;
}

/* 工具窗口 */
.tools-window {
  max-width: 1400px;
  margin: 0 auto;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(31, 58, 147, 0.1);
  display: flex;
  min-height: 700px;
  border: 1px solid #e4e7ed;
}

/* 左侧工具导航 */
.tools-sidebar {
  width: 280px;
  background: #f8f9ff;
  border-right: 1px solid #e4e7ed;
  border-radius: 12px 0 0 12px;
}

.sidebar-header {
  padding: 20px;
  border-bottom: 1px solid #e4e7ed;
  background: #1f3a93;
  color: white;
  border-radius: 12px 0 0 0;
}

.sidebar-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.tools-nav {
  padding: 10px 0;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-left: 3px solid transparent;
}

.nav-item:hover {
  background: rgba(31, 58, 147, 0.05);
}

.nav-item.active {
  background: rgba(31, 58, 147, 0.1);
  border-left-color: #1f3a93;
}

.nav-icon {
  font-size: 20px;
  width: 32px;
  margin-right: 12px;
}

.nav-title {
  font-weight: 600;
  color: #1f3a93;
  font-size: 14px;
  margin-bottom: 2px;
}

.nav-subtitle {
  font-size: 12px;
  color: #666;
}

/* 主内容区域 */
.main-content {
  flex: 1;
  padding: 30px;
  overflow-y: auto;
}

.tool-panel {
  height: 100%;
}

.panel-header {
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 2px solid #f0f0f0;
}

.panel-header h2 {
  margin: 0 0 8px 0;
  color: #1f3a93;
  font-size: 24px;
}

.panel-header p {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.panel-body {
  /* 内容区域样式 */
}

/* 结果区域 */
.results-section {
  margin-top: 30px;
}

.show-more-hint {
  text-align: center;
  color: #666;
  font-size: 12px;
  margin-top: 15px;
  font-style: italic;
}

.input-hint {
  font-size: 12px;
  color: #666;
  margin-top: 5px;
}

/* 图表区域 */
.chart-section {
  margin-top: 30px;
}

.chart-container {
  padding: 20px;
  background: #f8f9ff;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
  height: 400px;
  position: relative;
}

.stats-table {
  margin-top: 20px;
}

/* 右侧帮助面板 */
.help-panel {
  width: 320px;
  background: #fafbfc;
  border-left: 1px solid #e4e7ed;
  border-radius: 0 12px 12px 0;
}

.help-header {
  padding: 20px;
  border-bottom: 1px solid #e4e7ed;
  background: #f0f2f5;
  border-radius: 0 12px 0 0;
}

.help-header h3 {
  margin: 0;
  color: #1f3a93;
  font-size: 16px;
  font-weight: 600;
}

.help-content {
  padding: 20px;
  overflow-y: auto;
  max-height: 600px;
}

.help-section {
  margin-bottom: 25px;
}

.help-section h4 {
  margin: 0 0 10px 0;
  color: #1f3a93;
  font-size: 14px;
  font-weight: 600;
}

.help-section p {
  margin: 0 0 10px 0;
  font-size: 13px;
  line-height: 1.5;
  color: #555;
}

.help-section ul,
.help-section ol {
  margin: 0;
  padding-left: 20px;
  font-size: 13px;
  line-height: 1.5;
  color: #555;
}

.help-section li {
  margin-bottom: 5px;
}

/* Element Plus样式覆写 */
:deep(.el-form-item__label) {
  color: #1f3a93;
  font-weight: 600;
}

:deep(.el-button--primary) {
  background: #1f3a93;
  border-color: #1f3a93;
}

:deep(.el-button--primary:hover) {
  background: #2952b8;
  border-color: #2952b8;
}

:deep(.el-table th) {
  background: #f8f9ff !important;
  color: #1f3a93;
  font-weight: 600;
}

:deep(.el-divider__text) {
  color: #1f3a93;
  font-weight: 600;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .help-panel {
    width: 280px;
  }
}

@media (max-width: 768px) {
  .tools-window {
    flex-direction: column;
    min-height: auto;
  }

  .tools-sidebar {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #e4e7ed;
    border-radius: 12px 12px 0 0;
  }

  .help-panel {
    width: 100%;
    border-left: none;
    border-top: 1px solid #e4e7ed;
    border-radius: 0 0 12px 12px;
  }

  .main-content {
    padding: 20px;
  }

  .chart-container {
    height: 300px;
  }
}
</style>